import {AccountDetail} from 'Agent/Accounts/models/AccountDetail';
import {InteractiveFormStateDto} from 'Agent/AgentForms/Interactive/models/Interactive';
import {MarketingFormStateDto} from 'Agent/AgentForms/Marketing/models/Marketing';
import {PolicyApplicationRecordInfoDTO} from 'Agent/Policies/api';
import {AlertPayload} from 'Core/ducks/alerts';
import {IO} from 'fp-ts/lib/IO';
import {Option} from 'fp-ts/lib/Option';
import {FilterFieldDefinitions} from 'Shared/components/Table/Table';
import {createAction} from 'Shared/utilities/actionCreator';
import unionize, {ofType, UnionOf} from 'unionize';
import {PreregisteredUserFilter} from 'Users/ducks/userAdministration';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {UserClaims} from 'Users/models/Claims';

enum ModalActionType {
  HIDE_MODAL = 'modal/HIDE_MODAL',
  SHOW_MODAL = 'modal/SHOW_MODAL',
}

export const ModalState = unionize({
  AddAgent: ofType<{}>(),
  Alerts: ofType<AlertData>(),
  UsersRegistration: ofType<UserRegistrationData>(),
  RequestPolicyInfoReport: ofType<{userClaims: UserClaims; accountDetail: AccountDetail; email: string}>(),
  None: ofType<{}>(),
  SwitchTaxId: ofType<{agencyAssociations: AgentAgencyClaim[]}>(),
  UploadEnrollmentApplication: ofType<UploadEnrollmentApplicationData>(),
  UploadA360EnrollmentApplication: ofType<UploadA360EnrollmentApplicationData>(),
  CorrespondenceFilterModal: ofType<{}>(),
  MobileFilter: ofType<MobileFilter<any>>(),
  ClaimsConfirmation: ofType<{policyNumber: string; eisFlag: boolean}>(),
  DeleteBeneficiaryConfirmation: ofType<{policyNumber: string; id: string}>(),
  DeletePendingRegistrationConfirmation: ofType<{
    id: string;
    filter: PreregisteredUserFilter;
    encryptedIdentifier?: string;
  }>(),
  SSOConfirmation: ofType<{message: number}>(),
  ReviewSecurityQuestionsAndResetPassword: ofType<UserClaims>(),
  MarketingFormStates: ofType<MarketingFormStatesData>(),
  InteractiveFormStates: ofType<InteractiveFormStatesData>(),
  EnrollmentDetails: ofType<EnrollmentDetailsData>(),
});

export type ModalStateUnion = UnionOf<typeof ModalState>;

type UploadEnrollmentApplicationData = {
  accountId: string;
  accountNumber: string;
  servicingAgentNumber?: string;
};

type UploadA360EnrollmentApplicationData = {
  accountNumber: string;
  accountName: string;
};

type UserRegistrationData = {
  onClose: () => void;
};

type AlertData = {
  messages: AlertPayload[];
  onDismissAlerts: IO<void>;
};

export type EnrollmentDetailsData = {
  recordInfo: PolicyApplicationRecordInfoDTO[];
};

type MobileFilter<TFilter> = {
  filter: TFilter;
  filters?: FilterFieldDefinitions<TFilter>;
  totalResults: Option<number>;
};

type MarketingFormStatesData = {
  states: MarketingFormStateDto;
};

type InteractiveFormStatesData = {
  states: InteractiveFormStateDto;
};

export const showModal = (modalState: ModalStateUnion) => createAction(ModalActionType.SHOW_MODAL, {modalState});
export const hideModal = () => createAction(ModalActionType.HIDE_MODAL);

export const DEFAULT_STATE: ModalState = {
  modalState: ModalState.None(),
};

type ModalState = {
  modalState: ModalStateUnion;
};

type ModalAction = ReturnType<typeof showModal> | ReturnType<typeof hideModal>;

export default (state: ModalState = DEFAULT_STATE, action: ModalAction): ModalState => {
  switch (action.type) {
    case ModalActionType.SHOW_MODAL:
      return {
        ...state,
        modalState: action.payload.modalState,
      };
    case ModalActionType.HIDE_MODAL:
      return {
        ...state,
        modalState: ModalState.None(),
      };
    default:
      return state;
  }
};
